// attempt-exit-map.js -- SlipSync attempt exit codes for the attempt ledger.
// Single job: map a triggerSync exit id (+ context) to the coded ledger fields
// { stage, outcome, reason_code, warning_code, http_status } (+ the server's sync counts on a complete exit, #1659).
// Pure table, no I/O.
// Spec: docs/specs/2026-09-29-attempt-ledger.md §6a (authoritative exit mapping).
// The enum arrays are the CLIENT copy; attempt-ledger-enum-drift.test.js pins them to
// api/lib/attempt-log-validate.js and the client_attempts migration CHECK lists.

export const FLOWS = ['slipsync'];
export const BOOKS = ['mgm', 'dk', 'fan', 'wh'];
export const STAGES = ['webview_open', 'book_auth', 'fetch', 'parse', 'submit', 'server_response', 'complete'];
export const OUTCOMES = ['success', 'partial', 'empty', 'failed', 'abandoned', 'indeterminate'];
export const REASON_CODES = ['user_closed', 'bridge_cancelled', 'unmounted', 'webview_timeout', 'open_error', 'script_error', 'auth_error', 'api_error', 'not_available_in_state', 'partial_stopped', 'http_error', 'submit_timeout', 'response_body_timeout', 'network_error', 'unknown', 'server_partial'];
// book_answer_*: the book's LAST not-signed-in answer before a pre-submit exit (2026-10-18 visibility);
// http_status then holds the BOOK's status, not our server's.
export const WARNING_CODES = ['response_parse_failed', 'callback_failed', 'callback_timeout', 'last_synced_write_failed', 'book_answer_access_denied', 'book_answer_redirected', 'book_answer_http_status', 'book_answer_html_response', 'book_answer_http_error', 'book_answer_network_error', 'book_answer_unexpected_shape', 'book_answer_no_site_experience', 'book_answer_no_sportsbook_state', 'book_answer_auth_route_empty', 'book_answer_unknown'];
export const PLATFORMS = ['ios'];

// ── Book answer vocabulary (2026-10-18 visibility) ──────────────────────────────────────────
// Closed lists for what a book script reports when page 0 is not bets, and for the bridge trail.
// They live in this analytics leaf so analytics-scrub can close the PostHog enums without
// importing the bridge (spec §3 dependency rule); betmgm-client.js and cookie-bridge.js read them.
// Why page 0 was not bets (the script's auth_error `reason`).
export const BOOK_ANSWER_REASONS = Object.freeze(['access_denied', 'redirected', 'http_status', 'html_response', 'http_error', 'network_error', 'unexpected_shape', 'no_site_experience', 'no_sportsbook_state', 'auth_route_empty']);
// The response's content-type, as a class (never the header text).
export const CONTENT_TYPE_CLASSES = Object.freeze(['json', 'html', 'text', 'other', 'none']);
// The bridge's 'book_answer_<reason>' ledger warning for an answer; an unlisted reason is 'unknown'.
export const bookAnswerWarning = (reason) => `book_answer_${BOOK_ANSWER_REASONS.includes(reason) ? reason : 'unknown'}`;
// Path segments that may be logged as a route name (anything else is ':redacted' / 'other'): a
// segment can be a token, email or account id (Codex P1, PR #1293), so this list is closed.
export const ROUTE_SEGMENTS = Object.freeze([
  'mybets', 'my-bets', 'sports', 'sportsbook', 'login', 'logout', 'signin', 'sign-in',
  'signup', 'register', 'auth', 'account', 'myaccount', 'verify', 'verification', 'geolocation',
  'reset-password', 'promotions', 'home', 'error', 'maintenance', 'restricted',
]);
export const ROUTE_NAMES = Object.freeze(['root', 'other', ...ROUTE_SEGMENTS]);
// Every note the bridge writes to its trail (cookie-bridge.js note()); a test pins the source to this list.
export const TRAIL_NOTES = Object.freeze(['open', 'closed', 'cancelled', 'timeout', 'success', 'book_error', 'script_error', 'not_available', 'open_error', 'retry_script_error', 'signin_page', 'inject_ceiling', 'signed_in_no_bets']);
export const TRAIL_TOKENS = Object.freeze([
  ...TRAIL_NOTES,
  ...['unknown', ...BOOK_ANSWER_REASONS].map((r) => `not_signed_in:${r}`),
  ...ROUTE_NAMES.map((r) => `route:${r}`),
]);
// Coded class of a book script's api_error / script_error (book-error-class.js). PostHog only --
// not a ledger column, so no server or migration list mirrors it.
export const BOOK_ERROR_CLASSES = Object.freeze([
  'http_status', // the book answered a non-2xx other than 401/403 (status in httpStatus)
  'no_bets_list', // 2xx JSON without a bet list
  'first_page_fetch_error',
  'first_page_parse_error',
  'fetch_failed', // the request itself failed (network, blocked, cancelled)
  'not_available_in_state',
  'socket_closed',
  'frame_parse_failed',
  'script_exception',
  'no_detail', // the script posted no text (DraftKings api_error before #1711)
  'socket_ctor', // DraftKings: the WebSocket constructor threw (#1711)
  'frame_timeout', // DraftKings: no matching frame within 10 s (#1711)
  'rpc_error', // DraftKings: a JSON-RPC error frame; its code goes in rpc_code (#1711)
  'settled_fetch_error', // BetMGM: the settled call failed after an empty, good open read (2026-10-18)
  'other',
]);
// Where a partial read stopped (the script's stoppedAt), for PostHog.
// cap_25: DraftKings before #1714 (kept so older rows validate). page2_* and size_cap: DraftKings page 2 (#1714 §3.3).
export const STOPPED_AT = Object.freeze(['fetch_error', 'parse_error', 'settled_fetch_error', 'cap', 'cap_25',
  'page2_rpc_error', 'page2_not_array', 'page2_timeout', 'page2_closed', 'page2_empty', 'page2_repeat',
  'page2_unverified', 'page2_exception', 'size_cap']);

// §6a row numbers in comments.
export const EXIT = Object.freeze({
  UNMOUNTED_AFTER_BRIDGE: 'unmounted_after_bridge', // 1
  BRIDGE_CANCELLED: 'bridge_cancelled',             // 2
  USER_CLOSED: 'user_closed',                       // 3
  WEBVIEW_TIMEOUT: 'webview_timeout',               // 4
  OPEN_ERROR: 'open_error',                         // 5
  BRIDGE_SCRIPT_ERROR: 'bridge_script_error',       // 6
  AUTH_ERROR: 'auth_error',                         // 7
  NOT_AVAILABLE_IN_STATE: 'not_available_in_state', // 8
  PARSED_ERROR: 'parsed_error',                     // 9 (ctx.parsedType: api_error | script_error)
  EMPTY: 'empty',                                   // 10
  HTTP_ERROR: 'http_error',                         // 11 (ctx.status)
  COMPLETE: 'complete',                             // 12-17 (ctx.partial, ctx.warnings, ctx.status)
  SUBMIT_DEADLINE: 'submit_deadline',               // 18a/18b (ctx.responseReceived, ctx.status)
  UNMOUNTED_IN_SUBMIT: 'unmounted_in_submit',       // 19
  SUBMIT_THROW: 'submit_throw',                     // 20
  UNKNOWN: 'unknown',                               // 21 (ctx.lastStage)
  BOOK_NO_BETS: 'book_no_bets',                     // 22 signed in, N non-bets answers in a row (2026-10-18)
});

// "If several warnings apply (14-17), record the first in table order."
const WARNING_PRIORITY = ['response_parse_failed', 'callback_failed', 'callback_timeout', 'last_synced_write_failed'];

const httpStatus = (s) => (Number.isInteger(s) && s >= 100 && s <= 599 ? s : null);
// #1659: the server's result counts on a complete exit. All four valid or none: a malformed result is never partly
// trusted. ctx.counts is { imported, skipped, failed, untracked }. Slip counts are 0..1000 (a sync carries at most 50
// slips); untracked counts LEGS, 0..5000 (50 slips x 25 legs = 1250 today; pre-Codex F1).
export const MAX_SYNC_COUNT = 1000;
export const MAX_UNTRACKED_COUNT = 5000;
const isCount = (n, max = MAX_SYNC_COUNT) => Number.isInteger(n) && n >= 0 && n <= max;
function syncCounts(c) {
  if (!c || typeof c !== 'object') return null;
  const { imported, skipped, failed, untracked } = c;
  if (![imported, skipped, failed].every((n) => isCount(n)) || !isCount(untracked, MAX_UNTRACKED_COUNT)) return null;
  return { imported_count: imported, skipped_count: skipped, failed_count: failed, untracked_count: untracked };
}
const coded = (stage, outcome, reason_code, extra = {}) => ({ stage, outcome, reason_code, warning_code: null, http_status: null, ...extra });

// 2026-10-18 visibility: the bridge's LAST book answer (ctx.bookAnswer { reason, status }) goes on a pre-submit exit
// as warning_code book_answer_<reason> and the BOOK's http_status. Exits that reached our server keep their own
// warning and status: those columns already mean the server's answer there.
const BOOK_ANSWER_STAGES = new Set(['webview_open', 'book_auth', 'fetch', 'parse']);
function withBookAnswer(c, a) {
  if (!a || typeof a !== 'object' || !BOOK_ANSWER_STAGES.has(c.stage) || c.warning_code !== null || c.http_status !== null) return c;
  return { ...c, warning_code: bookAnswerWarning(a.reason), http_status: httpStatus(a.status) };
}

export function mapExit(exitId, ctx = {}) {
  return withBookAnswer(mapExitCode(exitId, ctx), ctx.bookAnswer);
}

function mapExitCode(exitId, ctx) {
  switch (exitId) {
    case EXIT.UNMOUNTED_AFTER_BRIDGE: return coded('webview_open', 'abandoned', 'unmounted');
    case EXIT.BRIDGE_CANCELLED: return coded('webview_open', 'abandoned', 'bridge_cancelled');
    case EXIT.USER_CLOSED: return coded('webview_open', 'abandoned', 'user_closed');
    case EXIT.WEBVIEW_TIMEOUT: return coded('book_auth', 'failed', 'webview_timeout'); // bridge hardening: a sign-in timeout
    case EXIT.OPEN_ERROR: return coded('webview_open', 'failed', 'open_error');
    case EXIT.BRIDGE_SCRIPT_ERROR: return coded('webview_open', 'failed', 'script_error');
    case EXIT.AUTH_ERROR: return coded('book_auth', 'failed', 'auth_error');
    case EXIT.NOT_AVAILABLE_IN_STATE: return coded('fetch', 'failed', 'not_available_in_state');
    case EXIT.PARSED_ERROR: return coded('fetch', 'failed', ctx.parsedType === 'script_error' ? 'script_error' : 'api_error');
    case EXIT.EMPTY: return coded('parse', 'empty', null);
    case EXIT.HTTP_ERROR: {
      const s = httpStatus(ctx.status);
      return s === null ? coded('server_response', 'failed', 'unknown') : coded('server_response', 'failed', 'http_error', { http_status: s });
    }
    case EXIT.COMPLETE: {
      const warnings = Array.isArray(ctx.warnings) ? ctx.warnings : [];
      const warning_code = WARNING_PRIORITY.find((w) => warnings.includes(w)) || null;
      const counts = syncCounts(ctx.counts);
      const extra = { warning_code, http_status: httpStatus(ctx.status), ...counts };
      // The book stopped early (partial_stopped) wins: those bets never reached the server. Otherwise a 200
      // that failed slips is a server-side partial (#1659), not a success.
      if (ctx.partial) return coded('complete', 'partial', 'partial_stopped', extra);
      if (counts && counts.failed_count > 0) return coded('complete', 'partial', 'server_partial', extra);
      return coded('complete', 'success', null, extra);
    }
    case EXIT.SUBMIT_DEADLINE:
      return ctx.responseReceived
        ? coded('server_response', 'indeterminate', 'response_body_timeout', { http_status: httpStatus(ctx.status) })
        : coded('submit', 'indeterminate', 'submit_timeout');
    case EXIT.UNMOUNTED_IN_SUBMIT: return coded('submit', 'abandoned', 'unmounted');
    case EXIT.SUBMIT_THROW: return coded('submit', 'failed', 'network_error');
    // Signed in (off a sign-in route) and the book kept answering something other than bets: a book API failure.
    case EXIT.BOOK_NO_BETS: return coded('fetch', 'failed', 'api_error');
    default:
      return coded(STAGES.includes(ctx.lastStage) ? ctx.lastStage : 'webview_open', 'failed', 'unknown');
  }
}

// Bridge hardening (spec §6): how each attempt's single recorded exit feeds the SlipSync guard. Unlisted -> error.
const CLASS_SUCCESS = new Set([EXIT.COMPLETE, EXIT.EMPTY]);
const CLASS_UNSUCCESSFUL = new Set([EXIT.USER_CLOSED, EXIT.WEBVIEW_TIMEOUT]);
const CLASS_NEUTRAL = new Set([EXIT.BRIDGE_CANCELLED, EXIT.UNMOUNTED_AFTER_BRIDGE, EXIT.UNMOUNTED_IN_SUBMIT,
  EXIT.NOT_AVAILABLE_IN_STATE, EXIT.HTTP_ERROR, EXIT.SUBMIT_DEADLINE, EXIT.SUBMIT_THROW]);
export function outcomeClassOf(exitId, { isCapture = false } = {}) {
  if (exitId === EXIT.EMPTY && isCapture) return 'unsuccessful'; // a capture window closed with no pages
  if (CLASS_SUCCESS.has(exitId)) return 'success';
  if (CLASS_UNSUCCESSFUL.has(exitId)) return 'unsuccessful';
  if (CLASS_NEUTRAL.has(exitId)) return 'neutral';
  return 'error';
}
